import { useState, useEffect } from 'react';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { Plus, Calendar, CheckCircle, Clock, DollarSign } from 'lucide-react';
import { supabase, getAuthHeaders } from '../../lib/supabase';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

interface Maintenance {
  id: string;
  asset_id: string;
  maintenance_type: string;
  description: string;
  scheduled_date: string;
  completed_date: string | null;
  status: string;
  technician: string;
  notes: string;
  cost: number;
  assets?: {
    asset_code: string;
  };
}

export default function Maintenance() {
  const [maintenances, setMaintenances] = useState<Maintenance[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [formData, setFormData] = useState({
    asset_id: '',
    maintenance_type: '',
    description: '',
    scheduled_date: '',
    technician: '',
    cost: ''
  });
  const [assets, setAssets] = useState<any[]>([]);

  useEffect(() => {
    loadMaintenances();
    loadAssets();
  }, []);

  const loadMaintenances = async () => {
    try {
      const { data, error } = await supabase
        .from('maintenances')
        .select(`
          *,
          assets (
            asset_code
          )
        `)
        .order('scheduled_date', { ascending: false });

      if (error) throw error;
      setMaintenances(data || []);
    } catch (error) {
      console.error('Erro ao carregar manutenções:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadAssets = async () => {
    try {
      const { data, error } = await supabase
        .from('assets')
        .select('id, asset_code')
        .order('asset_code');

      if (error) throw error;
      setAssets(data || []);
    } catch (error) {
      console.error('Erro ao carregar ativos:', error);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(
        `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/maintenance-operations`,
        {
          method: 'POST',
          headers,
          body: JSON.stringify({
            action: 'create',
            data: {
              ...formData,
              cost: parseFloat(formData.cost),
              status: 'pending'
            }
          })
        }
      );

      if (response.ok) {
        alert('Manutenção agendada com sucesso!');
        setShowAddModal(false);
        setFormData({
          asset_id: '',
          maintenance_type: '',
          description: '',
          scheduled_date: '',
          technician: '',
          cost: ''
        });
        loadMaintenances();
      }
    } catch (error) {
      console.error('Erro ao criar manutenção:', error);
      alert('Erro ao agendar manutenção');
    }
  };

  const handleComplete = async (id: string) => {
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(
        `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/maintenance-operations`,
        {
          method: 'POST',
          headers,
          body: JSON.stringify({
            action: 'update',
            maintenanceId: id,
            data: {
              status: 'completed',
              completed_date: new Date().toISOString().split('T')[0]
            }
          })
        }
      );

      if (response.ok) {
        alert('Manutenção concluída!');
        loadMaintenances();
      }
    } catch (error) {
      console.error('Erro ao concluir manutenção:', error);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'bg-amber-100 text-amber-800';
      case 'in_progress': return 'bg-blue-100 text-blue-800';
      case 'completed': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'pending': return 'Pendente';
      case 'in_progress': return 'Em Andamento';
      case 'completed': return 'Concluída';
      default: return status;
    }
  };

  return (
    <DashboardLayout>
      <div className="p-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Manutenção Preventiva</h1>
            <p className="text-gray-600 mt-2">Agendamento e controle de manutenções</p>
          </div>
          <button
            onClick={() => setShowAddModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer"
          >
            <Plus className="w-5 h-5" />
            Agendar Manutenção
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                <Clock className="w-5 h-5 text-amber-600" />
              </div>
              <span className="text-sm text-gray-600">Pendentes</span>
            </div>
            <p className="text-3xl font-bold text-gray-900">
              {maintenances.filter(m => m.status === 'pending').length}
            </p>
          </div>

          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-green-600" />
              </div>
              <span className="text-sm text-gray-600">Concluídas</span>
            </div>
            <p className="text-3xl font-bold text-gray-900">
              {maintenances.filter(m => m.status === 'completed').length}
            </p>
          </div>

          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center">
                <DollarSign className="w-5 h-5 text-teal-600" />
              </div>
              <span className="text-sm text-gray-600">Custo Total</span>
            </div>
            <p className="text-3xl font-bold text-gray-900">
              R$ {maintenances.reduce((sum, m) => sum + (m.cost || 0), 0).toFixed(2)}
            </p>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Ativo</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Tipo</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Data Agendada</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Técnico</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Custo</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Status</th>
                  <th className="px-6 py-4 text-right text-sm font-semibold text-gray-900">Ações</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {maintenances.map(maintenance => (
                  <tr key={maintenance.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4 text-sm text-gray-900 font-medium">
                      {maintenance.assets?.asset_code || '-'}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">{maintenance.maintenance_type}</td>
                    <td className="px-6 py-4 text-sm text-gray-600">
                      {format(new Date(maintenance.scheduled_date), 'dd/MM/yyyy', { locale: ptBR })}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">{maintenance.technician}</td>
                    <td className="px-6 py-4 text-sm text-gray-600">
                      R$ {maintenance.cost?.toFixed(2) || '0.00'}
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(maintenance.status)}`}>
                        {getStatusLabel(maintenance.status)}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right">
                      {maintenance.status === 'pending' && (
                        <button
                          onClick={() => handleComplete(maintenance.id)}
                          className="px-3 py-1 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm whitespace-nowrap cursor-pointer"
                        >
                          Concluir
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {showAddModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl max-w-2xl w-full p-6 max-h-[80vh] overflow-y-auto">
              <h3 className="text-xl font-bold text-gray-900 mb-6">Agendar Manutenção</h3>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Ativo</label>
                  <select
                    value={formData.asset_id}
                    onChange={(e) => setFormData({ ...formData, asset_id: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none"
                    required
                  >
                    <option value="">Selecione um ativo</option>
                    {assets.map(asset => (
                      <option key={asset.id} value={asset.id}>{asset.asset_code}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Tipo de Manutenção</label>
                  <input
                    type="text"
                    value={formData.maintenance_type}
                    onChange={(e) => setFormData({ ...formData, maintenance_type: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none"
                    placeholder="Ex: Preventiva, Corretiva"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Descrição</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none"
                    rows={3}
                    placeholder="Descreva a manutenção"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Data Agendada</label>
                  <input
                    type="date"
                    value={formData.scheduled_date}
                    onChange={(e) => setFormData({ ...formData, scheduled_date: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Técnico Responsável</label>
                  <input
                    type="text"
                    value={formData.technician}
                    onChange={(e) => setFormData({ ...formData, technician: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none"
                    placeholder="Nome do técnico"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Custo Estimado (R$)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.cost}
                    onChange={(e) => setFormData({ ...formData, cost: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none"
                    placeholder="0.00"
                    required
                  />
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowAddModal(false)}
                    className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    className="flex-1 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    Agendar
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
